/**
 * Componente para gestionar los vehículos asignados a un evento/presupuesto
 * Permite ver, añadir vehículos propios o de alquiler y eliminar asignaciones
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Truck, Plus, X, AlertTriangle, Car, Building2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { opcionesAlquilerJornadas } from '../utils/multieventoCabecera';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const claseBoton = 'h-[34px] px-3 rounded-lg border text-[13px] font-bold whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed';
const botonPrincipal = `${claseBoton} bg-duetto-primary border-duetto-primary text-white hover:bg-opacity-90`;
const formatEuros = (n) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(Number(n) || 0);
const SIN_JORNADAS = [];
const isoDe = (v) => String(v ?? '').slice(0, 10);
const diaMes = (iso) => (iso && iso.length >= 10 ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : '');

// Fechas que ocupa una furgoneta asignada (M-63, mismo criterio que el
// servidor y la hoja de material): con `jornada_id`, el día de esa jornada;
// con `fecha`, ese día; sin ninguna, todos los días del evento
const fechasDeAsignacion = (asignacion, jornadas, fechasEvento) => {
  if (asignacion?.jornada_id) {
    const j = (jornadas || []).find(x => x && x.id === asignacion.jornada_id);
    if (j && isoDe(j.fecha)) return [isoDe(j.fecha)];
  } else if (isoDe(asignacion?.fecha)) {
    return [isoDe(asignacion.fecha)];
  }
  return fechasEvento;
};

// onGuardado (opcional): 'inicio' | 'ok' | 'error' alrededor de cada guardado
// variante 'nuevo': diseño de tarjetas de la ficha nueva (mismas funciones)
// jornadas (multievento, M-63): la disponibilidad se mira en todos los días
// del evento y la furgoneta puede ir a todo el evento o a una jornada
const VehiculosAsignados = ({ presupuestoId, presupuestoNumero, fechaEvento, jornadas = SIN_JORNADAS, vehiculosActuales = [], onUpdate, inTab = false, onGuardado, variante = null, encolar = null }) => {
  const nuevo = variante === 'nuevo';
  const { token } = useAuth();
  const [vehiculos, setVehiculos] = useState(vehiculosActuales || []);
  // Respuesta de /vehiculos/disponibilidad/{fecha} de cada día del evento
  const [dispPorFecha, setDispPorFecha] = useState({});
  // Jornada de la asignación nueva ('' = todo el evento)
  const [jornadaSel, setJornadaSel] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [tipoAsignacion, setTipoAsignacion] = useState('propio'); // 'propio' o 'alquiler'
  const [selectedVehiculo, setSelectedVehiculo] = useState('');
  const [alquilerData, setAlquilerData] = useState({
    empresa_alquiler: '',
    coste_alquiler: 0,
    notas: ''
  });
  const [loading, setLoading] = useState(false);
  const [loadingDisponibilidad, setLoadingDisponibilidad] = useState(false);

  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  // Formato nuevo (`encolar`): el guardado va por la cola de la ficha (nunca dos
  // guardados del presupuesto a la vez). Sin `encolar`, como siempre.
  const enCola = async (clave, hacer) => {
    if (!encolar) return hacer();
    const r = await encolar(clave, async () => {
      try {
        return { res: await hacer() };
      } catch (error) {
        return { error };
      }
    });
    if (r && r.error) throw r.error;
    return r ? r.res : undefined;
  };

  // Actualizar cuando cambien vehiculosActuales
  useEffect(() => {
    setVehiculos(vehiculosActuales || []);
  }, [vehiculosActuales]);

  // Días del evento: los de las jornadas en un multievento (M-63); si no, la fecha del evento
  const jornadasValidas = useMemo(() => (Array.isArray(jornadas) ? jornadas.filter(j => j && j.id) : []), [jornadas]);
  const fechasEvento = useMemo(() => {
    const deJornadas = [...new Set(jornadasValidas.map(j => isoDe(j.fecha)).filter(Boolean))].sort();
    if (deJornadas.length > 0) return deJornadas;
    return isoDe(fechaEvento) ? [isoDe(fechaEvento)] : [];
  }, [jornadasValidas, fechaEvento]);
  const fechasClave = fechasEvento.join(',');
  const esMulti = jornadasValidas.length > 0;
  const opcionesJornada = useMemo(
    () => (esMulti ? opcionesAlquilerJornadas({ es_multi_evento: true, jornadas: jornadasValidas }) : []),
    [esMulti, jornadasValidas]
  );

  // Disponibilidad de cada día del evento (solo lectura)
  const fetchDisponibilidad = useCallback(async () => {
    const fechas = fechasClave ? fechasClave.split(',') : [];
    if (fechas.length === 0) return;
    setLoadingDisponibilidad(true);
    try {
      const cabeceras = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
      const respuestas = await Promise.all(fechas.map(async (f) => {
        const res = await fetch(`${API_URL}/api/vehiculos/disponibilidad/${f}`, { headers: cabeceras });
        return [f, await res.json()];
      }));
      setDispPorFecha(Object.fromEntries(respuestas));
    } catch (e) {
      console.error('Error cargando disponibilidad:', e);
    } finally {
      setLoadingDisponibilidad(false);
    }
  }, [fechasClave, token]);

  // Al abrir el modal o con furgonetas asignadas (para avisar de conflictos)
  useEffect(() => {
    if (fechasClave && (showAddModal || vehiculos.length > 0)) {
      fetchDisponibilidad();
    }
  }, [fechasClave, showAddModal, vehiculos.length, fetchDisponibilidad]);

  // Disponibles y ocupadas en los días de la asignación nueva: la jornada
  // elegida o todos los días del evento (ocupada si lo está en alguno)
  const { vehiculosDisponibles, vehiculosOcupados } = useMemo(() => {
    const jornada = jornadaSel ? jornadasValidas.find(j => j.id === jornadaSel) : null;
    const fechas = jornada && isoDe(jornada.fecha) ? [isoDe(jornada.fecha)] : fechasEvento;
    const todos = new Map();
    const ocupados = new Map();
    fechas.forEach((f) => {
      const data = dispPorFecha[f];
      if (!data) return;
      [...(data.disponibles || []), ...(data.ocupados || [])].forEach(v => { if (!todos.has(v.id)) todos.set(v.id, v); });
      (data.ocupados || []).forEach((o) => {
        const actual = ocupados.get(o.id) || { ...o, asignaciones: [] };
        (o.asignaciones || []).forEach(a => actual.asignaciones.push({ ...a, fecha: a.fecha || f }));
        actual.num_asignaciones = actual.asignaciones.length;
        ocupados.set(o.id, actual);
      });
    });
    return {
      vehiculosDisponibles: [...todos.values()].filter(v => !ocupados.has(v.id)),
      vehiculosOcupados: [...ocupados.values()],
    };
  }, [dispPorFecha, fechasEvento, jornadaSel, jornadasValidas]);

  // Conflictos de las furgonetas ya asignadas: otros eventos en los días que ocupan
  const conflictos = useMemo(() => {
    const res = {};
    vehiculos.forEach((v) => {
      if (!v.vehiculo_id || v.es_alquiler) return;
      const otros = [];
      fechasDeAsignacion(v, jornadasValidas, fechasEvento).forEach((f) => {
        const ocupado = (dispPorFecha[f]?.ocupados || []).find(o => o.id === v.vehiculo_id);
        (ocupado?.asignaciones || [])
          .filter(a => a.presupuesto_id !== presupuestoId)
          .forEach(a => otros.push({ ...a, fecha: a.fecha || f }));
      });
      if (otros.length > 0) res[v.vehiculo_id] = otros;
    });
    return res;
  }, [vehiculos, dispPorFecha, jornadasValidas, fechasEvento, presupuestoId]);

  // "#2301 (18/11, 12:00-17:30)" (la fecha solo en un multievento)
  const textoAsignacion = (a) => `#${a.presupuesto_numero} (${esMulti && a.fecha ? `${diaMes(a.fecha)}, ` : ''}${a.hora_inicio || '?'}-${a.hora_fin || '?'})`;
  const etiquetaJornada = (jid) => opcionesJornada.find(o => o.id === jid)?.etiqueta || 'Jornada';

  const handleAsignarVehiculo = async () => {
    if (tipoAsignacion === 'propio' && !selectedVehiculo) return;
    if (tipoAsignacion === 'alquiler' && !alquilerData.empresa_alquiler) return;
    
    setLoading(true);
    let guardando = false;
    try {
      const body = tipoAsignacion === 'alquiler' 
        ? {
            es_alquiler: true,
            empresa_alquiler: alquilerData.empresa_alquiler,
            coste_alquiler: alquilerData.coste_alquiler || 0,
            notas: alquilerData.notas
          }
        : {
            vehiculo_id: selectedVehiculo,
            es_alquiler: false,
            notas: ''
          };
      // Multievento (M-63): una jornada o, sin ella, todo el evento
      if (esMulti && jornadaSel) body.jornada_id = jornadaSel;

      if (onGuardado) { onGuardado('inicio'); guardando = true; }
      const res = await enCola('furgonetas', () => fetch(`${API_URL}/api/vehiculos/asignar-evento?presupuesto_id=${presupuestoId}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body)
      }));
      if (guardando) { onGuardado(res.ok ? 'ok' : 'error'); guardando = false; }

      if (res.ok) {
        const data = await res.json();
        const nuevosVehiculos = [...vehiculos, data.vehiculo_asignado];
        setVehiculos(nuevosVehiculos);
        onUpdate && onUpdate(nuevosVehiculos);
        
        // Mostrar warning si existe (furgoneta asignada a múltiples eventos)
        if (data.warning) {
          alert(data.warning);
        }
        
        // Reset form
        setShowAddModal(false);
        setSelectedVehiculo('');
        setAlquilerData({ empresa_alquiler: '', coste_alquiler: 0, notas: '' });
        setTipoAsignacion('propio');
        setJornadaSel('');
      } else {
        const error = await res.json();
        alert(error.detail || 'Error al asignar vehículo');
      }
    } catch (e) {
      if (guardando) onGuardado('error');
      console.error('Error asignando vehículo:', e);
      alert('Error al asignar vehículo');
    } finally {
      setLoading(false);
    }
  };

  const handleDesasignar = async (asignacionId) => {
    if (!window.confirm('¿Eliminar esta asignación de vehículo?')) return;
    
    let guardando = false;
    try {
      if (onGuardado) { onGuardado('inicio'); guardando = true; }
      const res = await enCola('furgonetas', () => fetch(`${API_URL}/api/vehiculos/desasignar-evento/${presupuestoId}/${asignacionId}`, {
        method: 'DELETE',
        headers
      }));
      if (guardando) { onGuardado(res.ok ? 'ok' : 'error'); guardando = false; }

      if (res.ok) {
        const nuevosVehiculos = vehiculos.filter(v => v.id !== asignacionId);
        setVehiculos(nuevosVehiculos);
        onUpdate && onUpdate(nuevosVehiculos);
      }
    } catch (e) {
      if (guardando) onGuardado('error');
      console.error('Error desasignando vehículo:', e);
    }
  };

  const vehiculosPropios = vehiculos.filter(v => !v.es_alquiler);
  const vehiculosAlquiler = vehiculos.filter(v => v.es_alquiler);
  const totalCosteAlquiler = vehiculosAlquiler.reduce((sum, v) => sum + (v.coste_alquiler || 0), 0);

  const modal = showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">Asignar Vehículo</h3>
              <button onClick={() => setShowAddModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Multievento (M-63): todo el evento o una jornada */}
            {esMulti && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Días</label>
                <select
                  value={jornadaSel}
                  onChange={(e) => { setJornadaSel(e.target.value); setSelectedVehiculo(''); }}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  data-testid="furgoneta-jornada"
                >
                  {opcionesJornada.map(o => (
                    <option key={o.id || 'evento'} value={o.id || ''}>{o.etiqueta}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Selector de tipo */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de vehículo</label>
              <div className="flex space-x-2">
                <button
                  onClick={() => setTipoAsignacion('propio')}
                  className={`flex-1 py-2 px-4 rounded-lg border text-sm font-medium transition-colors ${
                    tipoAsignacion === 'propio'
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <Truck className="w-4 h-4 inline mr-2" />
                  Propio
                </button>
                <button
                  onClick={() => setTipoAsignacion('alquiler')}
                  className={`flex-1 py-2 px-4 rounded-lg border text-sm font-medium transition-colors ${
                    tipoAsignacion === 'alquiler'
                      ? 'bg-orange-600 text-white border-orange-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <Building2 className="w-4 h-4 inline mr-2" />
                  Alquiler
                </button>
              </div>
            </div>

            {tipoAsignacion === 'propio' ? (
              <>
                {/* Info de fecha */}
                {fechasEvento.length > 0 && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg text-sm">
                    <p className="text-gray-600">
                      {esMulti ? 'Días que se comprueban: ' : 'Fecha del evento: '}
                      <span className="font-medium">
                        {esMulti
                          ? (jornadaSel
                            ? diaMes(isoDe(jornadasValidas.find(j => j.id === jornadaSel)?.fecha))
                            : fechasEvento.map(diaMes).join(', '))
                          : fechaEvento}
                      </span>
                    </p>
                    <p className="text-gray-500 text-xs mt-1">
                      {loadingDisponibilidad 
                        ? 'Verificando disponibilidad...' 
                        : `${vehiculosDisponibles.length} disponibles, ${vehiculosOcupados.length} ocupados`
                      }
                    </p>
                  </div>
                )}

                {/* Selector de vehículo propio */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Seleccionar vehículo</label>
                  
                  {/* Vehículos disponibles */}
                  {vehiculosDisponibles.length > 0 ? (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {vehiculosDisponibles
                        .filter(v => !vehiculos.some(va => va.vehiculo_id === v.id))
                        .map(v => (
                          <label 
                            key={v.id} 
                            className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                              selectedVehiculo === v.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="radio"
                              name="vehiculo"
                              value={v.id}
                              checked={selectedVehiculo === v.id}
                              onChange={(e) => setSelectedVehiculo(e.target.value)}
                              className="sr-only"
                            />
                            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                              <Truck className="w-4 h-4 text-green-600" />
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">{v.matricula}</p>
                              <p className="text-xs text-gray-500">{v.marca} {v.modelo}</p>
                            </div>
                            <span className="text-xs text-green-600 font-medium">Disponible</span>
                          </label>
                        ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-gray-500">
                      <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
                      <p className="text-sm">No hay vehículos disponibles</p>
                      <p className="text-xs">Considera alquilar uno</p>
                    </div>
                  )}

                  {/* Vehículos ocupados - AHORA SELECCIONABLES con warning */}
                  {vehiculosOcupados.length > 0 && (
                    <div className="mt-4">
                      <p className="text-xs text-amber-600 mb-2 font-medium">
                        Asignados a otros eventos (seleccionables si los horarios lo permiten):
                      </p>
                      <div className="space-y-2">
                        {vehiculosOcupados
                          .filter(v => !vehiculos.some(va => va.vehiculo_id === v.id))
                          .map(v => (
                            <label 
                              key={v.id} 
                              className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                                selectedVehiculo === v.id 
                                  ? 'border-amber-500 bg-amber-50' 
                                  : 'border-amber-200 bg-amber-50/50 hover:bg-amber-100'
                              }`}
                            >
                              <input
                                type="radio"
                                name="vehiculo"
                                value={v.id}
                                checked={selectedVehiculo === v.id}
                                onChange={(e) => setSelectedVehiculo(e.target.value)}
                                className="sr-only"
                              />
                              <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center mr-3">
                                <Truck className="w-4 h-4 text-amber-600" />
                              </div>
                              <div className="flex-1">
                                <p className="font-medium text-gray-900">{v.matricula}</p>
                                <p className="text-xs text-gray-500">{v.marca} {v.modelo}</p>
                                {v.asignaciones && v.asignaciones.length > 0 && (
                                  <div className="mt-1">
                                    {v.asignaciones.map((asig, idx) => (
                                      <p key={idx} className="text-xs text-amber-700">
                                        → {textoAsignacion(asig)}
                                      </p>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div className="text-right">
                                <span className="text-xs bg-amber-200 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                                  {v.num_asignaciones || 1} evento{(v.num_asignaciones || 1) > 1 ? 's' : ''}
                                </span>
                              </div>
                            </label>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                {/* Formulario de alquiler */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Empresa de alquiler *</label>
                    <input
                      type="text"
                      value={alquilerData.empresa_alquiler}
                      onChange={(e) => setAlquilerData({...alquilerData, empresa_alquiler: e.target.value})}
                      placeholder="Ej: Europcar, Enterprise..."
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Coste de alquiler (€)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={alquilerData.coste_alquiler}
                      onChange={(e) => setAlquilerData({...alquilerData, coste_alquiler: parseFloat(e.target.value) || 0})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
                    <textarea
                      value={alquilerData.notas}
                      onChange={(e) => setAlquilerData({...alquilerData, notas: e.target.value})}
                      placeholder="Tipo de vehículo, tamaño, características..."
                      rows={2}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Botones */}
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleAsignarVehiculo}
                disabled={loading || (tipoAsignacion === 'propio' && !selectedVehiculo) || (tipoAsignacion === 'alquiler' && !alquilerData.empresa_alquiler)}
                className={`px-4 py-2 rounded-lg text-white transition-colors ${
                  nuevo
                    ? 'bg-duetto-primary hover:bg-opacity-90 disabled:opacity-50 font-bold'
                    : (tipoAsignacion === 'alquiler' 
                    ? 'bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300' 
                    : 'bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300')
                }`}
              >
                {loading ? 'Asignando...' : 'Asignar Vehículo'}
              </button>
            </div>
          </div>
        </div>
  );

  // Formato nuevo: filas compactas en la tarjeta "Furgonetas"
  if (nuevo) {
    return (
      <div className="flex flex-col gap-3" data-testid="furgonetas-asignadas">
        {vehiculos.length === 0 ? (
          <div className="rounded-[10px] border px-3.5 py-3 text-sm bg-[#FDF0DC] border-[#F4D9AE] text-[#7A3A06]" data-testid="furgonetas-vacio">
            <strong>Sin furgonetas asignadas.</strong> Añade una furgoneta propia o de alquiler.
          </div>
        ) : (
          <div className="border border-[#E1E6E3] rounded-lg divide-y divide-[#EEF1EF]">
            {vehiculosPropios.map((v) => {
              const otros = conflictos[v.vehiculo_id] || [];
              return (
                <div key={v.id} className={`flex items-center justify-between gap-3 px-3 py-2 ${otros.length > 0 ? 'bg-[#FDF0DC]' : ''}`}>
                  <div className="min-w-0">
                    <span className="text-sm font-semibold text-[#16221D]">{v.matricula}</span>
                    <span className="ml-2 text-xs text-[#5B6B64]">{[v.marca, v.modelo].filter(Boolean).join(' ')} · propia{esMulti ? ` · ${v.jornada_id ? etiquetaJornada(v.jornada_id) : 'todo el evento'}` : ''}</span>
                    {otros.length > 0 && (
                      <span className="block text-xs text-[#7A3A06]">
                        {esMulti ? 'También en otro evento: ' : `En ${otros.length + 1} eventos ese día. También en: `}{otros.map(textoAsignacion).join(', ')}
                      </span>
                    )}
                  </div>
                  <button type="button" onClick={() => handleDesasignar(v.id)} className="text-xs font-semibold text-[#8A1C12] hover:underline" title="Eliminar asignación">
                    Quitar
                  </button>
                </div>
              );
            })}
            {vehiculosAlquiler.map((v) => (
              <div key={v.id} className="flex items-center justify-between gap-3 px-3 py-2">
                <div className="min-w-0">
                  <span className="text-sm font-semibold text-[#16221D]">Alquiler: {v.empresa_alquiler}{esMulti ? ` · ${v.jornada_id ? etiquetaJornada(v.jornada_id) : 'todo el evento'}` : ''}</span>
                  <span className="block text-xs text-[#5B6B64]">
                    {v.coste_alquiler > 0 ? formatEuros(v.coste_alquiler) : 'Sin coste indicado'}{v.notas ? ` · ${v.notas}` : ''}
                  </span>
                </div>
                <button type="button" onClick={() => handleDesasignar(v.id)} className="text-xs font-semibold text-[#8A1C12] hover:underline" title="Eliminar asignación">
                  Quitar
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-[#F7F9F8] px-3.5 py-3 text-sm">
          <span className="text-[#5B6B64]">
            {vehiculos.length === 0 ? 'Ninguna asignada' : `${vehiculos.length} ${vehiculos.length === 1 ? 'furgoneta' : 'furgonetas'}`}
            {totalCosteAlquiler > 0 ? ` · alquiler ${formatEuros(totalCosteAlquiler)}` : ''}
          </span>
          <button type="button" onClick={() => { setShowAddModal(true); fetchDisponibilidad(); }} className={botonPrincipal} data-testid="furgonetas-asignar">
            Asignar furgoneta
          </button>
        </div>
        {modal}
      </div>
    );
  }

  return (
    <div className={inTab ? '' : 'bg-white rounded-lg shadow-sm border border-gray-200 p-5'}>
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center space-x-2">
          <Truck className="w-5 h-5 text-blue-600" />
          <h3 className={`${inTab ? 'text-base' : 'text-lg'} font-bold text-gray-900`}>Furgonetas</h3>
          <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full">
            {vehiculos.length}
          </span>
        </div>
        <button
          onClick={() => { setShowAddModal(true); fetchDisponibilidad(); }}
          className="flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4 mr-1" />
          Asignar
        </button>
      </div>

      {/* Lista de vehículos asignados */}
      {vehiculos.length === 0 ? (
        <div className="text-center py-6 text-gray-500">
          <Truck className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p className="text-sm">No hay vehículos asignados</p>
          <p className="text-xs text-gray-400">Añade vehículos propios o de alquiler</p>
        </div>
      ) : (
        <div className="space-y-2">
          {/* Vehículos propios */}
          {vehiculosPropios.map((v) => {
            const tieneConflicto = conflictos[v.vehiculo_id] && conflictos[v.vehiculo_id].length > 0;
            const numEventos = tieneConflicto ? conflictos[v.vehiculo_id].length + 1 : 1;
            
            return (
              <div 
                key={v.id} 
                className={`flex items-center justify-between p-3 rounded-lg border ${
                  tieneConflicto 
                    ? 'bg-amber-50 border-amber-300' 
                    : 'bg-blue-50 border-blue-100'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    tieneConflicto ? 'bg-amber-100' : 'bg-blue-100'
                  }`}>
                    <Truck className={`w-4 h-4 ${tieneConflicto ? 'text-amber-600' : 'text-blue-600'}`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900">{v.matricula}</p>
                      {esMulti && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                          {v.jornada_id ? etiquetaJornada(v.jornada_id) : 'Todo el evento'}
                        </span>
                      )}
                      {tieneConflicto && (
                        <span className="text-xs bg-amber-200 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                          {numEventos} eventos
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">{v.marca} {v.modelo}</p>
                    {tieneConflicto && (
                      <div className="mt-1">
                        <p className="text-xs text-amber-700">
                          También en: {conflictos[v.vehiculo_id].map(textoAsignacion).join(', ')}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleDesasignar(v.id)}
                  className="p-1 text-red-500 hover:bg-red-50 rounded"
                  title="Eliminar asignación"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            );
          })}

          {/* Vehículos de alquiler */}
          {vehiculosAlquiler.map((v) => (
            <div key={v.id} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-100">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Building2 className="w-4 h-4 text-orange-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Alquiler - {v.empresa_alquiler}</p>
                  <p className="text-xs text-gray-500">
                    {v.coste_alquiler > 0 && <span className="text-orange-600 font-medium">{v.coste_alquiler}€</span>}
                    {v.notas && <span className="ml-2">{v.notas}</span>}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleDesasignar(v.id)}
                className="p-1 text-red-500 hover:bg-red-50 rounded"
                title="Eliminar asignación"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}

          {/* Resumen de costes de alquiler */}
          {totalCosteAlquiler > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Total alquiler:</span>
                <span className="font-bold text-orange-600">{totalCosteAlquiler.toFixed(2)}€</span>
              </div>
            </div>
          )}
        </div>
      )}

      {modal}
    </div>
  );
};

export default VehiculosAsignados;
